/**
 * The archive slider on About us.
 *
 * Seven panels stacked made an honest but very long page. One at a time, wide
 * enough to read, moving on its own, tells the same story in one screen.
 *
 * The track is a scroll container with scroll snapping rather than a
 * transform-driven carousel: a swipe on a phone, a trackpad flick and the
 * keyboard all work without being reimplemented, and if this script never
 * loads the panels are still there and still scrollable.
 *
 * It stops advancing whenever somebody is looking at a particular panel —
 * hovering, focused inside it, or mid-drag — because a slide that moves out
 * from under the sentence being read is worse than no slider at all. It also
 * stops when the tab is hidden, and never starts for a visitor who has asked
 * the system for reduced motion.
 */
(function () {
  'use strict';

  function init(root) {
    var track = root.querySelector('[data-tc-track]');
    if (!track) return;
    var slides = [].slice.call(track.querySelectorAll('[data-tc-slide]'));
    if (slides.length < 2) return;

    var dotsWrap = root.querySelector('[data-tc-dots]');
    var prev = root.querySelector('[data-tc-prev]');
    var next = root.querySelector('[data-tc-next]');
    var interval = Number(root.getAttribute('data-interval')) || 6000;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var index = 0;
    var timer = null;
    var held = false;

    var dots = slides.map(function (_, i) {
      if (!dotsWrap) return null;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'tc-slider__dot';
      b.setAttribute('aria-label', 'Panel ' + (i + 1) + ' of ' + slides.length);
      b.addEventListener('click', function () { go(i, true); });
      dotsWrap.appendChild(b);
      return b;
    });

    function paint() {
      dots.forEach(function (d, i) {
        if (d) d.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      // A panel scrolled out of view must not keep its links tabbable, or the
      // keyboard walks into slides nobody can see.
      slides.forEach(function (s, i) {
        s.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
    }

    /*
     * Mandatory snapping and a smooth programmatic scroll fight each other:
     * the snap engine cancels the animation and puts the track straight back
     * where it started, so the dots advance and the picture never moves.
     * Snapping is switched off for the length of the animation and restored
     * afterwards, which keeps the swipe behaviour it exists for.
     */
    var snapBack;
    function go(i, fromUser) {
      index = (i + slides.length) % slides.length;
      var left = slides[index].offsetLeft - track.offsetLeft;
      if (reduced) {
        track.scrollLeft = left;
      } else {
        track.style.scrollSnapType = 'none';
        track.scrollTo({ left: left, behavior: 'smooth' });
        clearTimeout(snapBack);
        snapBack = setTimeout(function () { track.style.scrollSnapType = ''; }, 700);
      }
      paint();
      if (fromUser) restart();
    }

    function start() {
      if (reduced || timer || held) return;
      timer = setInterval(function () { go(index + 1, false); }, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function restart() { stop(); start(); }

    function hold(on) { held = on; if (on) stop(); else start(); }

    root.addEventListener('pointerenter', function () { hold(true); });
    root.addEventListener('pointerleave', function () { hold(false); });
    root.addEventListener('focusin', function () { hold(true); });
    root.addEventListener('focusout', function () {
      if (!root.contains(document.activeElement)) hold(false);
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    if (prev) prev.addEventListener('click', function () { go(index - 1, true); });
    if (next) next.addEventListener('click', function () { go(index + 1, true); });

    // Somebody who swipes owns the position; follow them rather than fight.
    var settle;
    track.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () {
        var mid = track.scrollLeft + track.clientWidth / 2;
        var nearest = 0;
        var best = Infinity;
        slides.forEach(function (s, i) {
          var c = s.offsetLeft - track.offsetLeft + s.clientWidth / 2;
          var d = Math.abs(c - mid);
          if (d < best) { best = d; nearest = i; }
        });
        if (nearest !== index) { index = nearest; paint(); }
      }, 120);
    }, { passive: true });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1, true); }
    });

    paint();
    start();
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-tc-slider]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    [].slice.call(e.target.querySelectorAll('[data-tc-slider]')).forEach(init);
  });
})();
